/**
 * Copyright (C) Siemens AG 1996-2024 ALL RIGHTS RESERVED.
 */

import {
  Component,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
  inject,
} from '@angular/core';
import {firstValueFrom, Subscription} from 'rxjs';
import {ActivatedRoute} from '@angular/router';
import {PassportService} from '../../../../services/passport.service';
import {PassportDto} from '../../../../models/Passport';
import {SchemaDTO, UISchemaDTO} from '../../../../models/Schema';
import {IxModalSize} from '@siemens/ix';
import {ModalService} from '@siemens/ix-angular';
import {WindowSizeService} from '../../../../services/window-size.service';
import {LogoService} from '../../../shared/services/logo/logo.service';
import {detectProductIdStandard} from '../../../../utils/product-id-url';

@Component({
  selector: 'app-public-battery-pass-viewer',
  templateUrl: './public-battery-pass-viewer.component.html',
  styleUrl: './public-battery-pass-viewer.component.scss',
  standalone: false,
})
export class PublicBatteryPassViewerComponent implements OnInit, OnDestroy {
  private readonly route = inject(ActivatedRoute);
  private readonly passportService = inject(PassportService);
  private readonly modalService = inject(ModalService);
  private readonly platformLogo = inject(LogoService).resolve();

  /** Logo shown in the top bar: brand logo bound to the passport, else the platform default. */
  protected logo = this.platformLogo;
  /** `false` renders an empty logo slot for neutral brand domains (SP-02). */
  protected showLogo = true;

  @ViewChild('requestAccessModal', {read: TemplateRef})
  requestAccessModal!: TemplateRef<HTMLElement>;
  size: IxModalSize;

  private routeSubscription?: Subscription;
  passId = '';
  passport?: PassportDto;
  schema: SchemaDTO['data'];
  uiSchema: UISchemaDTO['data'];

  loading = true;
  passFound = true;

  ngOnInit() {
    // `url` (not `params`) so navigations between two Product ID links under
    // the same wildcard route, which carry no route params, re-resolve too.
    this.routeSubscription = this.route.url.subscribe(() => {
      void this.resolvePassport();
    });
  }

  private async resolvePassport(): Promise<void> {
    this.loading = true;
    try {
      // Product ID entry points (IEC 61406 long/short form, GS1 Digital Link)
      // forward the verbatim browser URL; everything else is a passport UUID.
      const productIdStandard = detectProductIdStandard(
        window.location.pathname,
      );
      if (productIdStandard) {
        this.passport = await firstValueFrom(
          this.passportService.loadOnePublicByProductId$(
            window.location.href,
            productIdStandard,
          ),
        );
        this.passId = this.passport.id;
      } else {
        const passId: string | undefined = this.route.snapshot.params['id'];
        // A bare Product ID prefix (e.g. `/id/` with nothing after it) lands
        // here without an `:id` param; show not-found without a backend call.
        if (!passId) {
          this.passFound = false;
          this.loading = false;
          return;
        }
        this.passId = passId;
        this.passport = await this.passportService.loadOnePublic(passId);
      }
      this.applyBranding();
      const schemaDTO = await this.passportService.loadSchema(
        this.passport?.metadata?.schemaId ?? '',
      );
      this.schema = schemaDTO.data;
      try {
        const uiSchemaDTO = await firstValueFrom(
          this.passportService.loadUiSchema$(
            this.passport?.metadata?.schemaId ?? '',
          ),
        );
        this.uiSchema = uiSchemaDTO.data;
      } catch {
        // uiSchema is optional — a missing or failing uiSchema does not block passport load
      }
      this.passFound = true;
    } catch {
      this.passFound = false;
    }
    this.loading = false;
  }

  /**
   * Applies the brand logo resolved by the backend for this passport. Branding is derived
   * from the passport's bound brand domain — never from the request host — so a passport
   * renders the same brand on every hostname it is opened under.
   */
  private applyBranding(): void {
    const branding = this.passport?.metadata?.branding;
    if (!branding) {
      this.logo = this.platformLogo;
      this.showLogo = true;
      return;
    }
    if (branding.logoUrl) {
      this.logo = {light: branding.logoUrl, dark: branding.logoUrl};
      this.showLogo = true;
    } else {
      this.showLogo = false;
    }
  }

  async openRequestAccessModal(event: MouseEvent): Promise<void> {
    event.preventDefault();

    if (WindowSizeService.windowSizeSmall()) {
      this.size = 'full-screen';
    } else {
      this.size = '840';
    }

    await this.modalService.open({
      size: this.size,
      data: this.size,
      content: this.requestAccessModal,
      centered: true,
      closeOnBackdropClick: true,
    });
  }

  ngOnDestroy() {
    this.routeSubscription?.unsubscribe();
  }
}
